// Shared parsing/writing utilities for simple 3D mesh formats: STL, OBJ, PLY (ASCII).
// Intermediate representation: flat vertex array + triangle index list.
// STEP and IGES are NOT handled here -- those are full CAD-kernel formats
// (NURBS surfaces, B-rep geometry) that need a real library (e.g.
// OpenCascade.js) to parse correctly. Attempting to hand-roll a parser for
// those risks silently producing wrong geometry, which is worse than not
// offering the tool.

export interface Mesh {
  vertices: number[]; // flat [x,y,z, x,y,z, ...]
  triangles: number[][]; // each entry is [i0, i1, i2] indices into vertices
}

export function meshStats(mesh: Mesh) {
  const vertexCount = mesh.vertices.length / 3;
  const triangleCount = mesh.triangles.length;
  let min = [Infinity, Infinity, Infinity];
  let max = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < mesh.vertices.length; i += 3) {
    for (let axis = 0; axis < 3; axis++) {
      const v = mesh.vertices[i + axis];
      if (v < min[axis]) min[axis] = v;
      if (v > max[axis]) max[axis] = v;
    }
  }
  const size =
    min[0] === Infinity
      ? [0, 0, 0]
      : [max[0] - min[0], max[1] - min[1], max[2] - min[2]];
  return { vertexCount, triangleCount, min, max, size };
}

// ---------- shared helpers ----------

/** Shortest decimal text that reads back as the same 32-bit float (0.1 stays "0.1", not 0.10000000149011612). */
export function fmtFloat32(v: number): string {
  if (!Number.isFinite(v)) return "0";
  const f = Math.fround(v);
  for (let p = 1; p <= 9; p++) {
    const t = Number(f.toPrecision(p));
    if (Math.fround(t) === f) return String(t === 0 ? 0 : t);
  }
  return String(f);
}

/** Unit normal of triangle (a,b,c); [0,0,0] for a degenerate triangle. */
export function triNormal(m: Mesh, a: number, b: number, c: number): [number, number, number] {
  const v = m.vertices;
  const ux = v[b * 3] - v[a * 3], uy = v[b * 3 + 1] - v[a * 3 + 1], uz = v[b * 3 + 2] - v[a * 3 + 2];
  const wx = v[c * 3] - v[a * 3], wy = v[c * 3 + 1] - v[a * 3 + 1], wz = v[c * 3 + 2] - v[a * 3 + 2];
  const nx = uy * wz - uz * wy, ny = uz * wx - ux * wz, nz = ux * wy - uy * wx;
  const len = Math.hypot(nx, ny, nz);
  return len > 0 ? [nx / len, ny / len, nz / len] : [0, 0, 0];
}

/**
 * Merge vertices that sit at the same position (STL and some exporters store every triangle's corners
 * separately, which triples the file size and hides which faces share an edge).
 * Also drops triangles that collapse to a line or point after merging.
 */
export function weldVertices(mesh: Mesh): Mesh & { removedDegenerate: number } {
  const st = meshStats(mesh);
  const diag = Math.hypot(st.size[0], st.size[1], st.size[2]) || 1;
  const q = diag * 1e-7; // far below anything a printer or viewer can show, above float32 noise
  const map = new Map<string, number>();
  const remap = new Array<number>(mesh.vertices.length / 3);
  const vertices: number[] = [];
  for (let i = 0; i < remap.length; i++) {
    const x = mesh.vertices[i * 3], y = mesh.vertices[i * 3 + 1], z = mesh.vertices[i * 3 + 2];
    const key = `${Math.round(x / q)},${Math.round(y / q)},${Math.round(z / q)}`;
    let id = map.get(key);
    if (id === undefined) { id = vertices.length / 3; map.set(key, id); vertices.push(x, y, z); }
    remap[i] = id;
  }
  const triangles: number[][] = [];
  let removedDegenerate = 0;
  for (const [a, b, c] of mesh.triangles) {
    const A = remap[a], B = remap[b], C = remap[c];
    if (A === B || B === C || A === C) { removedDegenerate++; continue; }
    triangles.push([A, B, C]);
  }
  return { vertices, triangles, removedDegenerate };
}

export function scaleMesh(mesh: Mesh, factor: number): Mesh {
  return factor === 1 ? mesh : { vertices: mesh.vertices.map((v) => v * factor), triangles: mesh.triangles };
}

/** 3D-printing sanity checks. Expects a welded mesh (call weldVertices first) so shared edges can be found. */
export function meshChecks(mesh: Mesh) {
  const edges = new Map<string, number>();
  const add = (a: number, b: number) => { const k = a < b ? `${a}_${b}` : `${b}_${a}`; edges.set(k, (edges.get(k) ?? 0) + 1); };
  let volume = 0, area = 0;
  const v = mesh.vertices;
  for (const [a, b, c] of mesh.triangles) {
    add(a, b); add(b, c); add(c, a);
    const ax = v[a * 3], ay = v[a * 3 + 1], az = v[a * 3 + 2], bx = v[b * 3], by = v[b * 3 + 1], bz = v[b * 3 + 2], cx = v[c * 3], cy = v[c * 3 + 1], cz = v[c * 3 + 2];
    volume += (ax * (by * cz - bz * cy) - ay * (bx * cz - bz * cx) + az * (bx * cy - by * cx)) / 6;
    area += 0.5 * Math.hypot((by - ay) * (cz - az) - (bz - az) * (cy - ay), (bz - az) * (cx - ax) - (bx - ax) * (cz - az), (bx - ax) * (cy - ay) - (by - ay) * (cx - ax));
  }
  let open = 0, nonManifold = 0;
  edges.forEach((n) => { if (n === 1) open++; else if (n > 2) nonManifold++; });
  return { watertight: mesh.triangles.length > 0 && open === 0 && nonManifold === 0, openEdges: open, nonManifoldEdges: nonManifold, volume: Math.abs(volume), flipped: volume < 0, area };
}

// ---------- STL ----------

/** Detects whether an STL buffer is ASCII or binary -- extracted as its
 * own exported helper so callers can know the source format upfront
 * (e.g. to disable a "convert to X" option when the file is already X),
 * not just have it happen implicitly inside parseSTL(). */
export function detectSTLFormat(buffer: ArrayBuffer): "ascii" | "binary" {
  const isBinaryBySize =
    buffer.byteLength >= 84 &&
    buffer.byteLength === 84 + new DataView(buffer).getUint32(80, true) * 50;

  // Many binary exporters start their 80-byte header with "solid", so the word alone proves nothing.
  // Real ASCII STL has a full "facet normal ... outer loop / vertex" run right after it.
  const text = new TextDecoder().decode(buffer.slice(0, Math.min(buffer.byteLength, 1024)));
  const looksAscii =
    /^\s*solid/i.test(text) && /facet\s+normal\s+\S+\s+\S+\s+\S+\s+outer\s+loop\s+vertex/i.test(text);

  return isBinaryBySize && !looksAscii ? "binary" : "ascii";
}

export type STLCheck =
  | { ok: true; format: "ascii" | "binary"; mesh: Mesh; triangleCount: number }
  | { ok: false; reason: string };

/**
 * Reads an STL and says plainly whether it is usable. A text file, an empty file, a cut-off binary file
 * or a file with no facets is "Not a valid STL" -- detectSTLFormat alone calls anything that is not
 * exactly binary "ascii", so it can never reject.
 */
export function validateSTL(buffer: ArrayBuffer): STLCheck {
  if (buffer.byteLength === 0) return { ok: false, reason: "Not a valid STL: the file is empty." };
  const format = detectSTLFormat(buffer);
  if (format === "ascii" && buffer.byteLength >= 84) {
    const count = new DataView(buffer).getUint32(80, true);
    const text = new TextDecoder().decode(buffer.slice(0, 1024));
    if (count > 0 && !/facet|vertex/i.test(text)) {
      const room = Math.floor((buffer.byteLength - 84) / 50);
      if (buffer.byteLength < 84 + count * 50 && count < 1e8) return { ok: false, reason: `Not a valid STL: it looks like a binary STL that is cut off (the header lists ${count.toLocaleString()} triangles, the file holds ${room.toLocaleString()}).` };
    }
  }
  let mesh: Mesh;
  try { mesh = parseSTL(buffer); } catch { return { ok: false, reason: "Not a valid STL: the file could not be read." }; }
  if (mesh.triangles.length === 0) return { ok: false, reason: format === "ascii" ? "Not a valid STL: no facets found. An ASCII STL needs lines like \"facet normal\" and \"vertex x y z\"." : "Not a valid STL: the file lists no triangles." };
  if (!mesh.vertices.every(Number.isFinite)) return { ok: false, reason: "Not a valid STL: it contains coordinates that are not numbers." };
  return { ok: true, format, mesh, triangleCount: mesh.triangles.length };
}

export function parseSTL(buffer: ArrayBuffer): Mesh {
  // Detect binary vs ASCII: binary STL has an 80-byte header then a 4-byte
  // triangle count, and the file size matches count*50+84 exactly.
  if (detectSTLFormat(buffer) === "binary") {
    return parseSTLBinary(buffer);
  }
  return parseSTLAscii(new TextDecoder().decode(buffer));
}

function parseSTLBinary(buffer: ArrayBuffer): Mesh {
  const dv = new DataView(buffer);
  const count = dv.getUint32(80, true);
  const vertices: number[] = [];
  const triangles: number[][] = [];
  let offset = 84;
  for (let i = 0; i < count; i++) {
    offset += 12; // skip normal
    const idx = vertices.length / 3;
    for (let v = 0; v < 3; v++) {
      vertices.push(
        dv.getFloat32(offset, true),
        dv.getFloat32(offset + 4, true),
        dv.getFloat32(offset + 8, true)
      );
      offset += 12;
    }
    triangles.push([idx, idx + 1, idx + 2]);
    offset += 2; // skip attribute byte count
  }
  return { vertices, triangles };
}

function parseSTLAscii(text: string): Mesh {
  const vertices: number[] = [];
  const triangles: number[][] = [];
  const vertexRegex = /vertex\s+([-\d.eE+]+)\s+([-\d.eE+]+)\s+([-\d.eE+]+)/g;
  let match;
  let current: number[] = [];
  while ((match = vertexRegex.exec(text)) !== null) {
    current.push(
      parseFloat(match[1]),
      parseFloat(match[2]),
      parseFloat(match[3])
    );
    if (current.length === 9) {
      const idx = vertices.length / 3;
      vertices.push(...current);
      triangles.push([idx, idx + 1, idx + 2]);
      current = [];
    }
  }
  return { vertices, triangles };
}

export function writeSTLAscii(mesh: Mesh, name = "mesh"): string {
  let out = `solid ${name}\n`;
  for (const [a, b, c] of mesh.triangles) {
    const n = triNormal(mesh, a, b, c);
    out += `  facet normal ${n.map(fmtFloat32).join(" ")}\n    outer loop\n`;
    for (const idx of [a, b, c]) {
      out += `      vertex ${fmtFloat32(mesh.vertices[idx * 3])} ${fmtFloat32(mesh.vertices[idx * 3 + 1])} ${fmtFloat32(mesh.vertices[idx * 3 + 2])}\n`;
    }
    out += "    endloop\n  endfacet\n";
  }
  out += `endsolid ${name}\n`;
  return out;
}

export function writeSTLBinary(mesh: Mesh): ArrayBuffer {
  const count = mesh.triangles.length;
  const buffer = new ArrayBuffer(84 + count * 50);
  const dv = new DataView(buffer);
  new Uint8Array(buffer, 0, 40).set(new TextEncoder().encode("Binary STL by FreeDailyPro"));
  dv.setUint32(80, count, true);
  let offset = 84;
  for (const [a, b, c] of mesh.triangles) {
    const n = triNormal(mesh, a, b, c);
    dv.setFloat32(offset, n[0], true);
    dv.setFloat32(offset + 4, n[1], true);
    dv.setFloat32(offset + 8, n[2], true);
    offset += 12;
    for (const idx of [a, b, c]) {
      dv.setFloat32(offset, mesh.vertices[idx * 3], true);
      dv.setFloat32(offset + 4, mesh.vertices[idx * 3 + 1], true);
      dv.setFloat32(offset + 8, mesh.vertices[idx * 3 + 2], true);
      offset += 12;
    }
    dv.setUint16(offset, 0, true);
    offset += 2;
  }
  return buffer;
}

// ---------- OBJ ----------

export function parseOBJ(text: string): Mesh {
  const vertices: number[] = [];
  const triangles: number[][] = [];
  for (const line of text.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (trimmed.startsWith("v ")) {
      const parts = trimmed.split(/\s+/);
      const x = parseFloat(parts[1]), y = parseFloat(parts[2]), z = parseFloat(parts[3]);
      if (![x, y, z].every(Number.isFinite)) throw new Error("OBJ has a vertex with a missing or invalid coordinate");
      vertices.push(x, y, z);
    } else if (trimmed.startsWith("f ")) {
      const count = vertices.length / 3;
      // "v", "v/vt", "v/vt/vn" or "v//vn". Positive indices are 1-based; negative ones count back from the vertices read so far.
      const idxs = trimmed.split(/\s+/).slice(1).map((p) => {
        const n = parseInt(p.split("/")[0], 10);
        return n < 0 ? count + n : n - 1;
      });
      if (idxs.some((i) => !Number.isInteger(i) || i < 0 || i >= count)) throw new Error("OBJ face points at a vertex that doesn't exist");
      for (let i = 1; i < idxs.length - 1; i++) triangles.push([idxs[0], idxs[i], idxs[i + 1]]);
    }
  }
  return { vertices, triangles };
}

export function writeOBJ(mesh: Mesh): string {
  let out = "# exported by FreeDailyPro 3D Mesh Converter\n";
  for (let i = 0; i < mesh.vertices.length; i += 3) {
    out += `v ${fmtFloat32(mesh.vertices[i])} ${fmtFloat32(mesh.vertices[i + 1])} ${fmtFloat32(mesh.vertices[i + 2])}\n`;
  }
  for (const [a, b, c] of mesh.triangles) {
    out += `f ${a + 1} ${b + 1} ${c + 1}\n`;
  }
  return out;
}

// ---------- PLY (ASCII only) ----------

const PLY_SIZES: Record<string, number> = { char: 1, int8: 1, uchar: 1, uint8: 1, short: 2, int16: 2, ushort: 2, uint16: 2, int: 4, int32: 4, uint: 4, uint32: 4, float: 4, float32: 4, double: 8, float64: 8 };

/** Reads PLY in any of its three encodings (ASCII, binary little-endian, binary big-endian). Reads x/y/z by name; ignores colours, normals and other properties. */
export function parsePLY(buffer: ArrayBuffer): Mesh {
  const bytes = new Uint8Array(buffer);
  const headEnd = (() => {
    const key = "end_header";
    const head = new TextDecoder("latin1").decode(bytes.subarray(0, Math.min(bytes.length, 65536)));
    const i = head.indexOf(key);
    if (i < 0) throw new Error("Not a PLY file: no end_header");
    let j = i + key.length;
    if (head[j] === "\r") j++;
    if (head[j] === "\n") j++;
    return { header: head.slice(0, i), body: j };
  })();
  const lines = headEnd.header.split(/\r?\n/).map((l) => l.trim());
  if (lines[0] !== "ply") throw new Error("Not a PLY file");
  const fmt = (lines.find((l) => l.startsWith("format ")) ?? "").split(/\s+/)[1];
  if (!["ascii", "binary_little_endian", "binary_big_endian"].includes(fmt)) throw new Error("Unknown PLY format");
  type Prop = { name: string; type: string; list?: { count: string; item: string } };
  const elements: { name: string; count: number; props: Prop[] }[] = [];
  for (const l of lines) {
    const t = l.split(/\s+/);
    if (t[0] === "element") elements.push({ name: t[1], count: parseInt(t[2], 10), props: [] });
    else if (t[0] === "property" && elements.length) {
      const el = elements[elements.length - 1];
      if (t[1] === "list") el.props.push({ name: t[4], type: t[3], list: { count: t[2], item: t[3] } });
      else el.props.push({ name: t[2], type: t[1] });
    }
  }
  const vertices: number[] = [];
  const triangles: number[][] = [];
  const faceFan = (idxs: number[]) => { for (let j = 1; j < idxs.length - 1; j++) triangles.push([idxs[0], idxs[j], idxs[j + 1]]); };

  if (fmt === "ascii") {
    const body = new TextDecoder().decode(bytes.subarray(headEnd.body)).split(/\r?\n/);
    let li = 0;
    for (const el of elements) {
      for (let n = 0; n < el.count; n++, li++) {
        const parts = (body[li] ?? "").trim().split(/\s+/);
        if (el.name === "vertex") {
          const xi = el.props.findIndex((p) => p.name === "x"), yi = el.props.findIndex((p) => p.name === "y"), zi = el.props.findIndex((p) => p.name === "z");
          vertices.push(parseFloat(parts[xi]), parseFloat(parts[yi]), parseFloat(parts[zi]));
        } else if (el.name === "face") {
          // Face lists can follow other properties; find the list property position.
          let pos = 0;
          for (const p of el.props) {
            if (p.list) { const cnt = Number(parts[pos++]); const idxs = parts.slice(pos, pos + cnt).map(Number); pos += cnt; if (p.name === "vertex_indices" || p.name === "vertex_index") faceFan(idxs); }
            else pos++;
          }
        }
      }
    }
  } else {
    const little = fmt === "binary_little_endian";
    const dv = new DataView(buffer, headEnd.body);
    let off = 0;
    const read = (type: string): number => {
      const size = PLY_SIZES[type];
      if (!size) throw new Error(`Unsupported PLY property type ${type}`);
      if (off + size > dv.byteLength) throw new Error("PLY file is truncated");
      let v: number;
      switch (type) {
        case "char": case "int8": v = dv.getInt8(off); break;
        case "uchar": case "uint8": v = dv.getUint8(off); break;
        case "short": case "int16": v = dv.getInt16(off, little); break;
        case "ushort": case "uint16": v = dv.getUint16(off, little); break;
        case "int": case "int32": v = dv.getInt32(off, little); break;
        case "uint": case "uint32": v = dv.getUint32(off, little); break;
        case "float": case "float32": v = dv.getFloat32(off, little); break;
        default: v = dv.getFloat64(off, little);
      }
      off += size;
      return v;
    };
    for (const el of elements) {
      for (let n = 0; n < el.count; n++) {
        const vals: Record<string, number> = {};
        for (const p of el.props) {
          if (p.list) {
            const cnt = read(p.list.count);
            const idxs: number[] = [];
            for (let k = 0; k < cnt; k++) idxs.push(read(p.list.item));
            if (el.name === "face" && (p.name === "vertex_indices" || p.name === "vertex_index")) faceFan(idxs);
          } else vals[p.name] = read(p.type);
        }
        if (el.name === "vertex") vertices.push(vals.x, vals.y, vals.z);
      }
    }
  }
  const count = vertices.length / 3;
  if (vertices.some((v) => !Number.isFinite(v))) throw new Error("PLY vertex data is invalid");
  if (triangles.some((t) => t.some((i) => !Number.isInteger(i) || i < 0 || i >= count))) throw new Error("PLY face points at a vertex that doesn't exist");
  return { vertices, triangles };
}

/** Kept for existing callers. */
export function parsePLYAscii(text: string): Mesh {
  return parsePLY(new TextEncoder().encode(text).buffer as ArrayBuffer);
}

export function writePLYBinary(mesh: Mesh): ArrayBuffer {
  const vc = mesh.vertices.length / 3;
  const head = new TextEncoder().encode(`ply\nformat binary_little_endian 1.0\ncomment exported by FreeDailyPro 3D Mesh Converter\nelement vertex ${vc}\nproperty float x\nproperty float y\nproperty float z\nelement face ${mesh.triangles.length}\nproperty list uchar int vertex_indices\nend_header\n`);
  const out = new ArrayBuffer(head.length + vc * 12 + mesh.triangles.length * 13);
  new Uint8Array(out).set(head);
  const dv = new DataView(out);
  let o = head.length;
  for (const v of mesh.vertices) { dv.setFloat32(o, v, true); o += 4; }
  for (const [a, b, c] of mesh.triangles) { dv.setUint8(o++, 3); dv.setInt32(o, a, true); dv.setInt32(o + 4, b, true); dv.setInt32(o + 8, c, true); o += 12; }
  return out;
}

export function writePLYAscii(mesh: Mesh): string {
  const vertexCount = mesh.vertices.length / 3;
  let out = `ply\nformat ascii 1.0\ncomment exported by FreeDailyPro 3D Mesh Converter\n`;
  out += `element vertex ${vertexCount}\nproperty float x\nproperty float y\nproperty float z\n`;
  out += `element face ${mesh.triangles.length}\nproperty list uchar int vertex_indices\nend_header\n`;
  for (let i = 0; i < mesh.vertices.length; i += 3) {
    out += `${fmtFloat32(mesh.vertices[i])} ${fmtFloat32(mesh.vertices[i + 1])} ${fmtFloat32(mesh.vertices[i + 2])}\n`;
  }
  for (const [a, b, c] of mesh.triangles) {
    out += `3 ${a} ${b} ${c}\n`;
  }
  return out;
}

/**
 * Writes a real, spec-compliant GLB (binary glTF 2.0) file -- verified
 * directly against the official Khronos glTF 2.0 binary container spec
 * rather than assumed: a 12-byte header (magic/version/length), a JSON
 * chunk (padded to a 4-byte boundary with spaces), and a binary chunk
 * (padded with zero bytes) holding the position and index buffers the
 * JSON chunk's accessors point to. Positions and normals only -- no
 * materials/textures/UVs, since the source formats (STL/OBJ/PLY) parsed
 * here don't carry that information either.
 */
export function writeGLB(mesh: Mesh): ArrayBuffer {
  const vertexCount = mesh.vertices.length / 3;
  const indexCount = mesh.triangles.length * 3;

  // Index buffer: use the smallest type that fits, per common glTF practice.
  const useShortIndices = vertexCount <= 65535;
  const indexArray = useShortIndices
    ? new Uint16Array(indexCount)
    : new Uint32Array(indexCount);
  let ii = 0;
  for (const [a, b, c] of mesh.triangles) {
    indexArray[ii++] = a;
    indexArray[ii++] = b;
    indexArray[ii++] = c;
  }

  const positionArray = new Float32Array(mesh.vertices);

  // POSITION accessor's min/max bounds are required by the glTF spec.
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (let i = 0; i < mesh.vertices.length; i += 3) {
    const x = mesh.vertices[i], y = mesh.vertices[i + 1], z = mesh.vertices[i + 2];
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
    if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
  }

  const positionBytes = positionArray.byteLength;
  const indexBytes = indexArray.byteLength;
  // Each bufferView must itself start on a 4-byte boundary.
  const indexOffset = Math.ceil(positionBytes / 4) * 4;
  const totalBinLength = indexOffset + indexBytes;

  const gltfJson = {
    asset: { version: "2.0", generator: "FreeDailyPro 3D Mesh Converter" },
    scene: 0,
    scenes: [{ nodes: [0] }],
    nodes: [{ mesh: 0 }],
    meshes: [
      {
        primitives: [
          {
            attributes: { POSITION: 0 },
            indices: 1,
            mode: 4, // TRIANGLES
          },
        ],
      },
    ],
    accessors: [
      {
        bufferView: 0,
        componentType: 5126, // FLOAT
        count: vertexCount,
        type: "VEC3",
        min: [minX, minY, minZ],
        max: [maxX, maxY, maxZ],
      },
      {
        bufferView: 1,
        componentType: useShortIndices ? 5123 : 5125, // UNSIGNED_SHORT / UNSIGNED_INT
        count: indexCount,
        type: "SCALAR",
      },
    ],
    bufferViews: [
      { buffer: 0, byteOffset: 0, byteLength: positionBytes, target: 34962 }, // ARRAY_BUFFER
      { buffer: 0, byteOffset: indexOffset, byteLength: indexBytes, target: 34963 }, // ELEMENT_ARRAY_BUFFER
    ],
    buffers: [{ byteLength: totalBinLength }],
  };

  const jsonString = JSON.stringify(gltfJson);
  const jsonBytes = new TextEncoder().encode(jsonString);
  // JSON chunk padded to a 4-byte boundary with ASCII spaces (0x20), per spec.
  const jsonPadding = (4 - (jsonBytes.length % 4)) % 4;
  const jsonChunkLength = jsonBytes.length + jsonPadding;

  // BIN chunk padded to a 4-byte boundary with zero bytes, per spec.
  const binPadding = (4 - (totalBinLength % 4)) % 4;
  const binChunkLength = totalBinLength + binPadding;

  const totalLength =
    12 + // header
    8 + jsonChunkLength + // JSON chunk header + data
    8 + binChunkLength; // BIN chunk header + data

  const buffer = new ArrayBuffer(totalLength);
  const view = new DataView(buffer);
  let offset = 0;

  // 12-byte header
  view.setUint32(offset, 0x46546c67, true); offset += 4; // magic "glTF"
  view.setUint32(offset, 2, true); offset += 4; // version
  view.setUint32(offset, totalLength, true); offset += 4; // total length

  // JSON chunk
  view.setUint32(offset, jsonChunkLength, true); offset += 4;
  view.setUint32(offset, 0x4e4f534a, true); offset += 4; // "JSON"
  new Uint8Array(buffer, offset, jsonBytes.length).set(jsonBytes);
  offset += jsonBytes.length;
  for (let i = 0; i < jsonPadding; i++) { view.setUint8(offset, 0x20); offset++; } // space-pad

  // BIN chunk
  view.setUint32(offset, binChunkLength, true); offset += 4;
  view.setUint32(offset, 0x004e4942, true); offset += 4; // "BIN\0"
  const binStart = offset;
  new Uint8Array(buffer, offset, positionArray.byteLength).set(
    new Uint8Array(positionArray.buffer, positionArray.byteOffset, positionArray.byteLength)
  );
  new Uint8Array(buffer, offset + indexOffset, indexArray.byteLength).set(
    new Uint8Array(indexArray.buffer, indexArray.byteOffset, indexArray.byteLength)
  );
  offset = binStart + totalBinLength;
  for (let i = 0; i < binPadding; i++) { view.setUint8(offset, 0); offset++; } // zero-pad

  return buffer;
}
